import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSeo } from '../utils/seo';
import { Button } from '../components/ui/button';
import {
  Home as HomeIcon,
  Calendar,
  ChevronRight,
  ArrowLeft,
  Share2,
  User,
  Menu,
  X,
  Calculator as CalculatorIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dynamic SEO based on the loaded article. Falls back to generic news SEO before loading.
  useSeo({
    title: article?.title
      ? `${article.title} | Vendetucasa.ar`
      : 'Noticia Inmobiliaria | Vendetucasa.ar',
    description: article?.excerpt
      || (article?.content ? article.content.replace(/<[^>]+>/g, '').slice(0, 155) : 'Noticias y análisis del mercado inmobiliario argentino por Vendetucasa.ar - Portal Inmobiliario.'),
    canonical: `https://vendetucasa.ar/noticias/${id}`,
    ogUrl: `https://vendetucasa.ar/noticias/${id}`,
    ogImage: article?.image_url || undefined,
  });

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${id}`);
        if (response.data.success) {
          setArticle(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        if (error.response?.status === 404) {
          setError('Artículo no encontrado');
        } else {
          setError('Error al cargar el artículo');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id]);

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E31C23]"></div>
          <p className="mt-4 text-slate-600">Cargando artículo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-blue-100 sticky top-0 z-50 shadow-sm">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_remax-ivancp/artifacts/1bsiu587_vendetucasa-ar-logo-transparente-header-blanco.png" 
                alt="Vendetucasa.ar" 
                className="h-10 w-auto object-contain"
              />
              <div className="text-[#003DA5]">
                <div className="font-semibold text-sm">Urbana III</div>
              </div>
            </Link>
          </div>
        </header>
        
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-700 mb-4">{error}</h1>
          <Button asChild className="bg-[#003DA5] hover:bg-[#002D7A]">
            <Link to="/noticias">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Noticias
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="relative">
              <svg viewBox="0 0 100 100" className="w-10 h-10">
                <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                <circle cx="25" cy="25" r="5" fill="white"/>
              </svg>
            </div>
            <span className="text-2xl font-bold">
              <span className="text-[#E31C23]">vende</span>
              <span className="text-slate-600">tu</span>
              <span className="text-[#E31C23]">casa</span>
              <span className="text-[#003DA5]">.ar</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-5">
            <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Sobre Mí</Link>
            <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Servicios</Link>
            <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Calculadora</Link>
            <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Barrios</Link>
            <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Propiedades</Link>
            <Link to="/noticias" className="text-[#E31C23] font-medium">Noticias</Link>
            <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">FAQ</Link>
            <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Contacto</Link>
          </nav>
          
          <div className="flex items-center gap-3">
            <Link 
              to="/calculadora" 
              className="calculator-pulse hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#003DA5] text-white hover:bg-[#E31C23] hover:scale-110 transition-all duration-200 shadow-md"
              title="Calculadora de Precios"
            >
              <CalculatorIcon className="w-5 h-5" />
            </Link>
            <Button asChild className="hidden sm:flex bg-[#E31C23] hover:bg-[#C11A1F] text-white">
              <Link to="/#appraisal-form">Conocé el Valor de tu Casa</Link>
            </Button>
            
            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="md:hidden text-[#003DA5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 py-4">
            <nav className="container mx-auto px-4 flex flex-col space-y-3">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Sobre Mí
              </Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Servicios
              </Link>
              <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Calculadora
              </Link>
              <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Barrios
              </Link>
              <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Propiedades
              </Link>
              <Link to="/noticias" className="text-[#E31C23] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Noticias
              </Link>
              <Link to="/#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                FAQ
              </Link>
              <Link to="/#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Contacto
              </Link>
              <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white w-full mt-2">
                <Link to="/#appraisal-form" onClick={() => setMobileMenuOpen(false)}>Conocé el Valor de tu Casa</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center text-sm text-slate-600">
          <Link to="/" className="hover:text-[#E31C23] flex items-center">
            <HomeIcon className="w-4 h-4 mr-1" />
            Inicio
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link to="/noticias" className="hover:text-[#E31C23]">Noticias</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#003DA5] font-medium line-clamp-1 max-w-[200px]">{article.title}</span>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Back Button */}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/noticias')}
              className="mb-6 text-slate-600 hover:text-[#003DA5]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Noticias
            </Button>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">
                {article.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-[#E31C23]" />
                  {new Date(article.created_at).toLocaleDateString('es-AR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </div>
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2 text-[#E31C23]" />
                  Vendetucasa.ar
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={shareArticle}
                  className="text-slate-600 hover:text-[#E31C23]"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Compartir
                </Button>
              </div>
            </header>

            {/* Featured Image */}
            {article.image_url && (
              <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={article.image_url} 
                  alt={article.title}
                  className="w-full h-auto max-h-[500px] object-cover"
                />
              </div>
            )}

            {/* Article Body */}
            <div 
              className="prose prose-lg prose-slate max-w-none
                prose-headings:text-[#003DA5] 
                prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-4
                prose-h2:text-2xl prose-h2:font-semibold prose-h2:mb-3 prose-h2:mt-8
                prose-h3:text-xl prose-h3:font-medium prose-h3:mb-2 prose-h3:mt-6
                prose-p:mb-4 prose-p:leading-relaxed
                prose-a:text-[#E31C23] prose-a:no-underline hover:prose-a:underline
                prose-strong:text-[#003DA5]
                prose-ul:my-4 prose-li:my-1
                prose-img:rounded-lg prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Bottom Navigation */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <Button asChild variant="outline" className="border-[#003DA5] text-[#003DA5]">
                  <Link to="/noticias">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Ver más noticias
                  </Link>
                </Button>
                <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white">
                  <Link to="/#appraisal-form">
                    Solicitar Análisis de Mercado
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#003DA5] text-white py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <svg viewBox="0 0 100 100" className="w-16 h-16">
                    <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                    <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                    <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                    <circle cx="25" cy="25" r="5" fill="white"/>
                  </svg>
                </div>
                <span className="text-4xl font-bold">
                  <span className="text-white">vende</span>
                  <span className="text-blue-200">tu</span>
                  <span className="text-white">casa</span>
                  <span className="text-blue-300">.ar</span>
                </span>
              </div>
            </div>
            <p className="text-blue-100 mb-2">Vendetucasa.ar - Portal Inmobiliario</p>
            <p className="text-blue-200 text-sm">
              © {new Date().getFullYear()} Vendetucasa.ar - Todos los derechos reservados
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticleDetail;
